import { useState, useMemo, useEffect } from 'react';
import { DateFilter, FilterType } from './DateFilter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Trophy, Medal, Award, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

type SortField = 'totalTickets' | 'resolutionRate' | 'avgSentiment' | 'positiveEmotions' | 'negativeEmotions';
type SortDirection = 'asc' | 'desc';

export interface AgentPerformanceSummariserProps {
  agentPerformance?: AgentPerformance[];
  onDateRangeChange: (range: { start: Date; end: Date } | null) => void;
}

export interface AgentPerformance {
  agentId: string;
  agentName: string;
  totalTickets: number;
  solvedTickets: number;
  resolutionRate: number;
  avgSentiment: number;
  sentimentBreakdown: { [key: string]: number };
  emotionBreakdown: { [key: string]: number };
  positiveEmotions: number;
  negativeEmotions: number;
  summary: string;
  rank: number;
}

export function AgentPerformanceSummariser({
  agentPerformance = [],
  onDateRangeChange = () => {}
}: AgentPerformanceSummariserProps) {

  const [sortField, setSortField] = useState<SortField>('resolutionRate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [rankingCriteria, setRankingCriteria] = useState<'overall' | 'resolution' | 'sentiment'>('overall');

  const [currentFilter, setCurrentFilter] = useState<{
    type: FilterType;
    date: Date | null;
  }>({ type: 'monthly', date: new Date() });

  const [expandedSummaries, setExpandedSummaries] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const { type, date } = currentFilter;
    if (!date) {
      onDateRangeChange(null);
      return;
    }

    const selectedDate = new Date(date);
    let start: Date;
    let end: Date;

    switch (type) {
      case 'weekly':
        start = new Date(selectedDate);
        start.setDate(selectedDate.getDate() - selectedDate.getDay());
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'monthly':
        start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        break;
      case 'daily':
      default:
        start = selectedDate;
        end = selectedDate;
        break;
    }
    
    end.setHours(23, 59, 59, 999);
    onDateRangeChange({ start, end });
  }, [currentFilter, onDateRangeChange]);

  const sortedData = useMemo(() => {
    const data = [...agentPerformance];
    const ranked = [...data].sort((a, b) => {
      switch (rankingCriteria) {
        case 'resolution':
          return b.resolutionRate - a.resolutionRate;
        case 'sentiment':
          return b.avgSentiment - a.avgSentiment;
        case 'overall':
        default:
          const scoreA = (a.resolutionRate * 0.6) + (a.avgSentiment * 40);
          const scoreB = (b.resolutionRate * 0.6) + (b.avgSentiment * 40);
          return scoreB - scoreA;
      }
    });

    ranked.forEach((agent, index) => (agent.rank = index + 1));

    return ranked.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      return sortDirection === 'asc'
        ? aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        : aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    });
  }, [agentPerformance, sortField, sortDirection, rankingCriteria]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const handleFilterChange = (filterType: FilterType, date: Date | null) => {
    setCurrentFilter({ type: filterType, date: date });
  };
  
  const formatTitleDate = () => {
    const { type, date } = currentFilter;
    if (!date) return 'all time';
    
    switch (type) {
      case 'daily':
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      case 'weekly':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `the week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      case 'monthly':
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      default:
        return 'the selected period';
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-gray-400" />;
      case 3: return <Award className="w-5 h-5 text-orange-600" />;
      default: return <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">{rank}</span>;
    }
  };

  const getSentimentColor = (score: number) => {
    if (score > 0.5) return 'text-green-600';
    if (score < -0.5) return 'text-red-600';
    return 'text-gray-600';
  };

  const getSentimentBadgeColor = (score: number) => {
    if (score > 0.5) return 'bg-green-100 text-green-800';
    if (score < -0.5) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between flex-wrap gap-4">
          {/* Left Side: Title and Subtitle */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Agent Performance Summary</h3>
            <p className="text-gray-600 mt-1">Comparative analysis for {formatTitleDate()}</p>
          </div>
          
          {/* --- FIX 1 START: Moved controls to a single container on the right --- */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Ranking Criteria:</label>
              <Select value={rankingCriteria} onValueChange={(value: any) => setRankingCriteria(value)}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="overall">Overall Performance</SelectItem>
                  <SelectItem value="resolution">Resolution Rate</SelectItem>
                  <SelectItem value="sentiment">Sentiment Score</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DateFilter onFilterChange={handleFilterChange} />
          </div>
           {/* --- FIX 1 END --- */}
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Rank</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead className="font-medium text-gray-700 w-80">Summary</TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('totalTickets')}>
                <div className="flex items-center gap-2">Total Tickets {getSortIcon('totalTickets')}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('resolutionRate')}>
                <div className="flex items-center gap-2">Resolution Rate {getSortIcon('resolutionRate')}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('avgSentiment')}>
                <div className="flex items-center gap-2">Avg Sentiment {getSortIcon('avgSentiment')}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('positiveEmotions')}>
                <div className="flex items-center gap-2 w-16">Positive Emotions {getSortIcon('positiveEmotions')}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('negativeEmotions')}>
                <div className="flex items-center gap-2 w-16">Negative Emotions {getSortIcon('negativeEmotions')}</div>
              </TableHead>
              <TableHead>Performance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* --- FIX 2 START: Correct conditional rendering logic --- */}
              {sortedData.length > 0 ? (
                sortedData.map(agent => {
                  const isExpanded = expandedSummaries[agent.agentId];
                  const shouldTruncate = agent.summary.length > 150;

                  const rawSummary = agent.summary.trim();
                  const parts = rawSummary.split(/\n{1,2}/);
                  const firstString = parts[0] || '';
                  const secondString = parts.slice(1).join('\n') || '';

                  return (
                    <TableRow key={agent.agentId}>
                      <TableCell>
                        <div className="flex items-center justify-center">{getRankIcon(agent.rank)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">{agent.agentName}</div>
                          <div className="text-sm text-gray-500">{agent.agentId}</div>
                        </div>
                      </TableCell>

                      <TableCell className="align-top whitespace-normal break-words w-80">
                        <div className="w-full break-words">
                          <p className="text-sm text-gray-700 whitespace-pre-line break-words">
                            {isExpanded || !shouldTruncate ? (
                              <>
                                {firstString}
                                <br />
                                <br />
                                {secondString}
                              </>
                            ) : (
                              <>{`${firstString.slice(0, 150)}...`}</>
                            )}
                          </p>
                          {shouldTruncate && (
                            <button
                              className="text-xs text-blue-600 hover:underline mt-1"
                              onClick={() =>
                                setExpandedSummaries(prev => ({
                                  ...prev,
                                  [agent.agentId]: !prev[agent.agentId],
                                }))
                              }
                            >
                              {isExpanded ? 'Read less' : 'Read more'}
                            </button>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="font-medium">{agent.totalTickets}</div>
                        <div className="text-sm text-gray-500">{agent.solvedTickets} solved</div>
                      </TableCell>

                      <TableCell>
                        <div className="font-medium">{agent.resolutionRate.toFixed(1)}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ width: `${Math.min(agent.resolutionRate, 100)}%` }}
                          />
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className={`font-medium ${getSentimentColor(agent.avgSentiment)}`}>
                          {agent.avgSentiment.toFixed(2)}
                        </div>
                        <Badge variant="secondary" className={getSentimentBadgeColor(agent.avgSentiment)}>
                          {agent.avgSentiment > 0.5
                            ? 'Positive'
                            : agent.avgSentiment < -0.5
                            ? 'Negative'
                            : 'Neutral'}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <div className="text-green-600 font-medium w-16">
                          {agent.positiveEmotions}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="text-red-600 font-medium w-16">
                          {agent.negativeEmotions}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {agent.rank <= 3 && (
                            <Badge variant="secondary" className="w-fit">
                              Top Performer
                            </Badge>
                          )}
                          {agent.resolutionRate >= 90 && (
                            <Badge
                              variant="secondary"
                              className="bg-green-100 text-green-800 w-fit"
                            >
                              High Resolution
                            </Badge>
                          )}
                          {agent.avgSentiment > 1 && (
                            <Badge
                              variant="secondary"
                              className="bg-blue-100 text-blue-800 w-fit"
                            >
                              Great Sentiment
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center">
                    <div className="text-center py-8 text-gray-500">
                      <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No agent performance data found for the selected period.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
             {/* --- FIX 2 END --- */}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
